import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { IconDotLarge } from "@/assets/icons/geist/IconDotLarge";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { IconWarningCircleFilled } from "@/assets/icons/geist/IconWarningCircleFilled";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { duration, formatMediumTime } from "@/format/time";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useStreamLogs } from "@/hooks/use-stream-logs";
import { useTriggerOnRunning } from "@/hooks/use-trigger-on-running";
import { type PlainEnvironment } from "@/queries/environment-queries";
import { type LogGroup, type LogLine, NO_LOG_GROUP } from "@/routes/environments/log-streams/log-groups";
import { formatError } from "@/utils/errors";
import Anser from "anser";
import { LogSectionOutcome } from "gitpod-next-api/gitpod/v1/environment_logs_pb";
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useThrottledCallback } from "use-debounce";

type EnvironmentLogsProps = {
    environment?: PlainEnvironment;
    filterLogLinesWithoutTimestamps: boolean;
    logsUrl?: string;
    maxLines?: number;
};

export const EnvironmentLogs: FC<EnvironmentLogsProps & PropsWithClassName> = ({
    className,
    environment,
    logsUrl,
    filterLogLinesWithoutTimestamps,
}) => {
    const logContainerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const logEndRef = useRef<HTMLDivElement>(null);
    const [isAutoScroll, setIsAutoScroll] = useState(true);
    const [lastScrollTop, setLastScrollTop] = useState(0);

    const { selectedEditor } = useGetEditorForEnv(environment?.id);

    const { logGroups, loadingMessage, error } = useStreamLogs({ environment, logsUrl, maxLinesPerGroup: 10000 });

    const openEditor = useOpenEditor();
    useTriggerOnRunning(environment?.status?.phase, () => !!selectedEditor && openEditor(selectedEditor, environment));

    // Handle auto-scroll
    const debouncedScroll = useThrottledCallback((scrollContainer: HTMLDivElement, scrollTop: number) => {
        scrollContainer.scrollTo({ top: scrollTop, behavior: "smooth" });
    }, 500);

    // Handle scroll event
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || !logEndRef.current) {
            return;
        }
        const { scrollTop, offsetHeight, scrollHeight } = scrollContainerRef.current;
        const bottom = scrollHeight - offsetHeight;

        // Disable auto scroll if the user scrolls up.
        // Only if there is any scrollable content - this ensure we still auto-scroll when re-fetching logs
        const scrolledBack = scrollTop < lastScrollTop;
        setLastScrollTop(scrollTop);
        if (scrolledBack && scrollHeight > 0) {
            setIsAutoScroll(false);

            // Cancel any pending/throttled autoscroll events.
            debouncedScroll.cancel();

            // Scroll once instantly to stop any ongoing smooth scroll.
            scrollContainerRef.current.scrollTo({ top: scrollTop, behavior: "instant" });
        } else if (scrollTop >= bottom - 100) {
            setIsAutoScroll(true);
        }
    }, [debouncedScroll, lastScrollTop]);

    const updateHeight = useCallback(() => {
        if (logContainerRef.current) {
            const topPosition = logContainerRef.current.getBoundingClientRect().top;
            const viewportHeight = window.innerHeight - 32;
            logContainerRef.current.style.height = `${viewportHeight - topPosition}px`;
        }
    }, []);

    useEffect(() => {
        if (isAutoScroll && scrollContainerRef.current && logEndRef.current) {
            const scrollContainer = scrollContainerRef.current;
            const logEnd = logEndRef.current;

            // Calculate the scroll position needed to bring logEnd into view
            const desiredScrollTop = logEnd.offsetTop - scrollContainer.offsetTop;

            // Smoothly scroll to the desired position
            debouncedScroll(scrollContainer, desiredScrollTop);
        }
    }, [logGroups, isAutoScroll, debouncedScroll]);

    // Update height on mount and window resize
    useEffect(() => {
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [updateHeight]);

    const isLegacyRunner =
        logGroups && Object.keys(logGroups).length === 1 && logGroups[NO_LOG_GROUP]?.lines?.length > 0;
    const legacyLogLines = logGroups?.[NO_LOG_GROUP]?.lines;

    return (
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-xl bg-always-dark p-4 text-always-light">
            {(error || loadingMessage) && (
                <div className="absolute bottom-0 flex w-full flex-col items-center p-4">
                    <ErrorMessage error={error} />
                    {loadingMessage && (
                        <span className="text-sm" data-testid="logs-loading-message">
                            {loadingMessage}
                        </span>
                    )}
                </div>
            )}
            <div className={cn(className, "relative shrink grow")} ref={logContainerRef}>
                <div
                    className="scrollbar-hide absolute top-0 max-h-full w-full overflow-y-auto"
                    onScroll={handleScroll}
                    ref={scrollContainerRef}
                >
                    {!isLegacyRunner && logGroups ? (
                        Object.values(logGroups).map((logGroup, i) => (
                            <LogGroup
                                key={i}
                                logGroup={logGroup}
                                filterLogLinesWithoutTimestamps={filterLogLinesWithoutTimestamps}
                            />
                        ))
                    ) : (
                        <LogLines
                            lines={legacyLogLines || []}
                            filterLogLinesWithoutTimestamps={filterLogLinesWithoutTimestamps}
                        />
                    )}
                    <div ref={logEndRef} />
                </div>
            </div>
        </div>
    );
};

const LogGroup: FC<{ logGroup: LogGroup; filterLogLinesWithoutTimestamps: boolean }> = ({
    logGroup,
    filterLogLinesWithoutTimestamps,
}) => {
    const location = useLocation();
    const isInProgress = logGroup.outcome === undefined;
    const isContinuous = logGroup.continuous;
    const failedOrCancelled =
        logGroup.outcome === LogSectionOutcome.FAILURE || logGroup.outcome === LogSectionOutcome.CANCELLED;
    const isDeepLinkToThisGroup = location.hash === `#${logGroup.id}`;
    const hasDeepLinkToAnotherGroup = location.hash && !isDeepLinkToThisGroup;

    const [expanded, setExpanded] = useState<{ expanded: boolean; byUser: boolean }>({
        byUser: isDeepLinkToThisGroup,
        expanded:
            !hasDeepLinkToAnotherGroup && !isContinuous && (isInProgress || failedOrCancelled || isDeepLinkToThisGroup),
    });

    const [copiedAll, setCopiedAll] = useState(false);
    const { toast } = useToast();

    const copyWholeGroup = useCallback(async () => {
        setCopiedAll(true);
        try {
            const textToCopy = logGroup.lines
                .filter((l) => l.type === "SectionLogLine" || l.type === "LegacyLogLine")
                .map((l) => l.line)
                .join("\n");

            await navigator.clipboard.writeText(textToCopy);
            toast({ title: "Copied to clipboard" });
        } catch (err) {
            setCopiedAll(false);
            toast({
                title: "Failed to copy to clipboard",
                description: formatError(err),
            });
        } finally {
            // Reset copiedAll after 2s, for the checkmark to revert
            setTimeout(() => setCopiedAll(false), 2000);
        }
    }, [logGroup.lines, toast]);

    // Automatically collapse log groups when they complete, unless the user has manually expanded them or they've failed
    useEffect(() => {
        if (expanded.expanded && !isInProgress && !expanded.byUser && logGroup.outcome === LogSectionOutcome.SUCCESS) {
            setExpanded({ expanded: false, byUser: false });
        }
    }, [expanded, setExpanded, isInProgress, logGroup, location]);

    if (logGroup.id == NO_LOG_GROUP) {
        return;
    }

    let icon: ReactNode;
    if (logGroup.outcome === undefined && !logGroup.continuous) {
        icon = <IconSpinner size="lg" className="animate-spin text-content-yield" />;
    } else if (logGroup.outcome === undefined && logGroup.continuous) {
        icon = <IconDotLarge size="lg" className="animate-pulse text-content-positive" />;
    } else if (logGroup.outcome === LogSectionOutcome.SUCCESS) {
        icon = <IconCheckCircle size="lg" className="text-content-positive" />;
    } else if (logGroup.outcome === LogSectionOutcome.CANCELLED) {
        icon = (
            <Tooltip content="This action was cancelled">
                <div>
                    <IconWarningCircle size="lg" className="text-content-tertiary dark:text-content-secondary" />
                </div>
            </Tooltip>
        );
    } else {
        icon = <IconWarningCircleFilled size="lg" className="text-content-negative" />;
    }
    return (
        <div className="w-full" id={logGroup.id}>
            <div
                className={cn(
                    "z-10 my-1 flex h-9 w-full items-center justify-between rounded-lg px-2 py-1",
                    expanded.expanded && "sticky top-0 bg-content-secondary dark:bg-content-tertiary",
                )}
            >
                <div className="flex items-center gap-4">
                    <Button
                        variant={"ghost"}
                        size="tiny"
                        className="size-5 border-0"
                        onClick={() => setExpanded({ expanded: !expanded.expanded, byUser: true })}
                        aria-label={expanded.expanded ? "Collapse log group" : "Expand log group"}
                        LeadingIcon={expanded.expanded ? IconChevronDown : IconChevronRight}
                    />
                    <div className="flex min-h-6 min-w-6 items-center justify-center">{icon}</div>
                    <span className="font-mono text-sm text-always-light">{logGroup.title}</span>
                </div>
                <div className="flex items-center gap-3">
                    {logGroup.secondsElapsed && (
                        <span className="font-mono text-sm text-always-light">{duration(logGroup.secondsElapsed)}</span>
                    )}
                    <Tooltip content="Copy all lines in this group">
                        <Button
                            variant="ghost"
                            size="tiny"
                            className={cn("size-5 border-0", {
                                "disabled:text-green-500": copiedAll,
                            })}
                            LeadingIcon={copiedAll ? IconCheck : IconCopy}
                            disabled={copiedAll}
                            onClick={copyWholeGroup}
                            aria-label="Copy all lines"
                        />
                    </Tooltip>
                </div>
            </div>
            <div className={cn("px-2", expanded.expanded ? "visible" : "hidden")}>
                <LogLines lines={logGroup.lines} filterLogLinesWithoutTimestamps={filterLogLinesWithoutTimestamps} />
            </div>
        </div>
    );
};

const LogLines: FC<{ lines: LogLine[]; filterLogLinesWithoutTimestamps: boolean }> = ({
    lines,
    filterLogLinesWithoutTimestamps,
}) => {
    const [copiedLineIndex, setCopiedLineIndex] = useState<number | null>(null);
    const { toast } = useToast();

    const handleCopyToClipboard = useCallback(
        async (line: string, index: number) => {
            setCopiedLineIndex(index);
            try {
                await navigator.clipboard.writeText(line);
                toast({ title: "Copied to clipboard" });
            } catch (error) {
                setCopiedLineIndex(null);
                toast({
                    title: "Failed to copy to clipboard",
                    description: formatError(error),
                });
            } finally {
                // Reset after 2s so checkmark reverts
                setTimeout(() => {
                    setCopiedLineIndex((prev) => (prev === index ? null : prev));
                }, 2000);
            }
        },
        [toast],
    );

    return (
        <>
            {lines.map((line, i) => {
                if (
                    (line.type === "SectionLogLine" || line.type === "LegacyLogLine") &&
                    (!filterLogLinesWithoutTimestamps || (filterLogLinesWithoutTimestamps && line.timestamp))
                ) {
                    const isCopied = copiedLineIndex === i;
                    return (
                        <div key={i} className="group relative flex items-start gap-3">
                            {line.timestamp && (
                                <span className="min-w-fit font-mono text-sm text-gray-500">
                                    {formatMediumTime(line.timestamp)}
                                </span>
                            )}
                            <span
                                className="grow whitespace-pre font-mono text-sm"
                                dangerouslySetInnerHTML={{ __html: Anser.ansiToHtml(line.line) }}
                            />
                            <div className="absolute right-0 flex items-center">
                                <Tooltip content="Copy to clipboard">
                                    <Button
                                        variant="ghost"
                                        size="tiny"
                                        className={cn("size-5 border-0 opacity-0 group-hover:opacity-100", {
                                            "disabled:text-green-500": isCopied,
                                        })}
                                        LeadingIcon={isCopied ? IconCheck : IconCopy}
                                        disabled={isCopied}
                                        onClick={() => handleCopyToClipboard(line.line, i)}
                                        aria-label="Copy to clipboard"
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    );
                }
            })}
        </>
    );
};
